import {
  Box,
  Typography,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface MethodStepProps {
  phone: string;
  onSelectOtp: () => void;
  onSelectPassword: () => void;
}

/**
 * Shown right after the phone number is verified.
 * Lets the user choose how they'd like to sign in.
 */
export default function MethodStep({ phone, onSelectOtp, onSelectPassword }: MethodStepProps) {
  const theme = useTheme();

  const maskedPhone = phone.length >= 10
    ? `+91 XXXXXX${phone.slice(-4)}`
    : phone;

  const options = [
    {
      id: 'login-with-otp-option',
      icon: <SmsOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />,
      title: 'Login with OTP',
      description: `Send a one-time code to ${maskedPhone}`,
      onClick: onSelectOtp,
    },
    {
      id: 'login-with-password-option',
      icon: <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />,
      title: 'Login with password',
      description: 'Use your username and password',
      onClick: onSelectPassword,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <VerifiedUserOutlinedIcon sx={{ color: 'success.main', fontSize: 28 }} />
      </Box>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        Number verified
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Choose how you'd like to sign in.
      </Typography>

      <Stack spacing={2}>
        {options.map((opt) => (
          <Box
            key={opt.id}
            id={opt.id}
            role="button"
            tabIndex={0}
            onClick={opt.onClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') opt.onClick();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              cursor: 'pointer',
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: '10px',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {opt.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                {opt.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.description}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: 'text.disabled' }} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
